import { forwardRef, type SelectHTMLAttributes } from 'react'

export interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
  label?: string
  hint?: string
  error?: string
  options: SelectOption[]
  value: string | null | undefined
  onValueChange: (value: string) => void
  /**
   * Sentinel value rendered when the current value is empty/null.
   * Persists PROJECT_MEMORY §6 even though we use native <select> —
   * keeps our upstream callers safe if we later swap in Radix Select.
   */
  emptySentinel?: string
  emptyLabel?: string
  allowEmpty?: boolean
}

const NONE = '__none__'

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    options,
    value,
    onValueChange,
    emptySentinel = NONE,
    emptyLabel = '—',
    allowEmpty = false,
    id,
    className = '',
    ...rest
  },
  ref,
) {
  const selId = id || rest.name || `sel-${Math.random().toString(36).slice(2)}`
  const safeValue = value == null || value === '' ? emptySentinel : value

  const finalOptions = allowEmpty
    ? [{ value: emptySentinel, label: emptyLabel }, ...options]
    : options

  return (
    <div className={`field ${error ? 'field-error' : ''}`}>
      {label ? <label htmlFor={selId}>{label}</label> : null}
      <select
        id={selId}
        ref={ref}
        className={`input ${className}`}
        value={safeValue}
        onChange={(e) => {
          const v = e.target.value
          onValueChange(v === emptySentinel ? '' : v)
        }}
        {...rest}
      >
        {finalOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && !error ? <small className="hint">{hint}</small> : null}
      {error ? <small className="err">{error}</small> : null}
    </div>
  )
})
